import { useState, useEffect, useRef } from "react";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Plus, Edit, Trash2, UtensilsCrossed, ChevronDown, ChevronUp, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { BilingualInput, BilingualTextarea } from "../../components/BilingualField";
import { getLocalized } from "../../utils/localized";

export default function OwnerMenu() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [dietaryTags, setDietaryTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCat, setShowAddCat] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [expandedCat, setExpandedCat] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => { fetchMenu(); }, []);

  const fetchMenu = async () => {
    try {
      const [catRes, itemsRes, dietRes] = await Promise.all([
        axios.get(`${API}/owner/menu/categories`),
        axios.get(`${API}/owner/menu/items`),
        axios.get(`${API}/taxonomy/dietary-tags`),
      ]);
      setCategories(catRes.data);
      setItems(itemsRes.data);
      setDietaryTags(dietRes.data || []);
      if (catRes.data.length > 0) setExpandedCat(catRes.data[0].category_id);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const tagLabel = (slug) => {
    const dt = dietaryTags.find(x => x.slug === slug);
    if (dt) return (lang === "el" && dt.name_el) || t(`dietary_tags.${dt.slug}`, { defaultValue: dt.name });
    return t(`dietary_tags.${slug}`, { defaultValue: slug });
  };

  const handleDeleteCat = async (catId) => {
    if (!window.confirm(t("owner_menu:confirm_delete_category", { defaultValue: "Delete this category and all its items?" }))) return;
    try {
      await axios.delete(`${API}/owner/menu/categories/${catId}`);
      setCategories(categories.filter(c => c.category_id !== catId));
      setItems(items.filter(i => i.category_id !== catId));
      toast.success(t("owner_menu:category_deleted", { defaultValue: "Category deleted" }));
    } catch (error) { toast.error(t("owner_menu:delete_failed", { defaultValue: "Failed to delete" })); }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm(t("owner_menu:confirm_delete_item", { defaultValue: "Delete this menu item?" }))) return;
    try {
      await axios.delete(`${API}/owner/menu/items/${itemId}`);
      setItems(items.filter(i => i.item_id !== itemId));
      toast.success(t("owner_menu:item_deleted", { defaultValue: "Item deleted" }));
    } catch (error) { toast.error(t("owner_menu:delete_failed", { defaultValue: "Failed to delete" })); }
  };

  const handleImageUpload = async (itemId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/owner/menu/items/${itemId}/image`, formData);
      setItems(items.map(i => i.item_id === itemId ? { ...i, image_url: res.data.image_url } : i));
      toast.success(t("owner_menu:image_uploaded", { defaultValue: "Image uploaded" }));
    } catch { toast.error(t("owner_menu:image_upload_failed", { defaultValue: "Failed to upload image" })); }
  };

  if (loading) return <div className="animate-pulse bg-muted h-64 rounded-xl" />;

  return (
    <div data-testid="owner-menu-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground">{t("owner_menu:title")}</h1>
        <div className="flex gap-2">
          <Dialog open={showAddCat} onOpenChange={setShowAddCat}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="menu-add-category-btn"><Plus className="h-4 w-4 mr-2" /> {t("owner_menu:category", { defaultValue: "Category" })}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("owner_menu:add_category", { defaultValue: "Add Category" })}</DialogTitle></DialogHeader>
              <CategoryForm onSuccess={c => { setCategories([...categories, c]); setShowAddCat(false); }} />
            </DialogContent>
          </Dialog>
          {categories.length > 0 && (
            <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
              <DialogTrigger asChild>
                <Button data-testid="menu-add-item-btn"><Plus className="h-4 w-4 mr-2" /> {t("owner_menu:item", { defaultValue: "Item" })}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("owner_menu:add_item", { defaultValue: "Add Menu Item" })}</DialogTitle></DialogHeader>
                <ItemForm categories={categories} dietaryTags={dietaryTags} onSuccess={i => { setItems([...items, i]); setShowAddItem(false); }} />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {editingItem && (
        <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("owner_menu:edit_item", { defaultValue: "Edit Item" })}</DialogTitle></DialogHeader>
            <ItemForm categories={categories} dietaryTags={dietaryTags} item={editingItem} onSuccess={updated => {
              setItems(items.map(i => i.item_id === updated.item_id ? updated : i));
              setEditingItem(null);
            }} />
          </DialogContent>
        </Dialog>
      )}

      {categories.length === 0 && items.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border border-border">
          <UtensilsCrossed className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t("owner_menu:no_categories")}</h3>
          <p className="text-muted-foreground mb-4">{t("owner_menu:empty_help", { defaultValue: "Start by creating categories like \"Starters\", \"Mains\", \"Desserts\"." })}</p>
          <Button onClick={() => setShowAddCat(true)}><Plus className="h-4 w-4 mr-2" /> {t("owner_menu:add_first_category", { defaultValue: "Add First Category" })}</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {categories.map(cat => {
            // Match items by category_id OR by case-insensitive category-name
            // string — covers admin-created items that may not yet carry a
            // category_id pointing to this owner-defined category.
            const catName = (cat.name || "").trim().toLowerCase();
            const catItems = items.filter(i =>
              i.category_id === cat.category_id ||
              (!i.category_id && (i.category || "").trim().toLowerCase() === catName)
            );
            const isExpanded = expandedCat === cat.category_id;
            return (
              <div key={cat.category_id} className="bg-card rounded-xl border border-border overflow-hidden" data-testid={`menu-category-${cat.category_id}`}>
                <div className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-muted/20" onClick={() => setExpandedCat(isExpanded ? null : cat.category_id)}>
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                    <h3 className="font-medium text-foreground">{getLocalized(cat, "name", lang) || cat.name}</h3>
                    <span className="text-xs text-muted-foreground">({catItems.length} {t("owner_menu:items_count", { defaultValue: "items" })})</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleDeleteCat(cat.category_id); }}>
                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                  </Button>
                </div>
                {isExpanded && (
                  <div className="border-t border-border">
                    {catItems.length > 0 ? catItems.map(item => (
                      <OwnerMenuItemRow key={item.item_id} item={item} lang={lang} t={t} tagLabel={tagLabel}
                        onEdit={() => setEditingItem(item)} onDelete={() => handleDeleteItem(item.item_id)} onImageUpload={handleImageUpload} />
                    )) : (
                      <p className="px-5 py-4 text-sm text-muted-foreground">{t("owner_menu:no_items_yet", { defaultValue: "No items yet. Add items to this category." })}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Orphan items: have neither category_id nor a string that matches
              any owner category. Group them under "Other" so they're not
              silently invisible. */}
          {(() => {
            const known = new Set(categories.map(c => (c.name || "").trim().toLowerCase()));
            const orphans = items.filter(i =>
              !categories.some(c => c.category_id === i.category_id) &&
              !known.has((i.category || "").trim().toLowerCase())
            );
            if (orphans.length === 0) return null;
            return (
              <div className="bg-card rounded-xl border border-border overflow-hidden" data-testid="menu-orphans-bucket">
                <div className="px-5 py-4">
                  <h3 className="font-medium text-foreground">{t("owner_menu:other_category", { defaultValue: "Other" })}</h3>
                  <span className="text-xs text-muted-foreground">({orphans.length} {t("owner_menu:items_count", { defaultValue: "items" })})</span>
                </div>
                <div className="border-t border-border">
                  {orphans.map(item => (
                    <OwnerMenuItemRow key={item.item_id} item={item} lang={lang} t={t} tagLabel={tagLabel}
                      onEdit={() => setEditingItem(item)} onDelete={() => handleDeleteItem(item.item_id)} onImageUpload={handleImageUpload} />
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function OwnerMenuItemRow({ item, lang, t, tagLabel, onEdit, onDelete, onImageUpload }) {
  const imgInputRef = useRef(null);
  const imgUrl = item.image_url
    ? (item.image_url.startsWith("http") ? item.image_url : `${API.replace('/api', '')}${item.image_url}`)
    : null;
  const displayName = getLocalized(item, "name", lang) || item.name;
  const displayDesc = getLocalized(item, "description", lang) || item.description;
  return (
    <div className="flex items-center gap-3 px-5 py-3 border-b border-border last:border-b-0 hover:bg-muted/20" data-testid={`menu-item-${item.item_id}`}>
      <div
        className="w-12 h-12 rounded-lg bg-muted overflow-hidden shrink-0 border border-border cursor-pointer relative group"
        onClick={() => imgInputRef.current?.click()}
        title={t("owner_menu:upload_image", { defaultValue: "Upload image" })}
        data-testid={`menu-item-image-${item.item_id}`}
      >
        {imgUrl ? (
          <img src={imgUrl} alt={displayName} className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ImagePlus className="h-4 w-4" />
          </div>
        )}
        <input ref={imgInputRef} type="file" accept="image/*" className="hidden"
          onChange={e => { if (e.target.files?.[0]) onImageUpload(item.item_id, e.target.files[0]); }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{displayName}</span>
          {item.is_available === false && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded">{t("owner_menu:unavailable", { defaultValue: "Unavailable" })}</span>}
        </div>
        {displayDesc && <p className="text-xs text-muted-foreground mt-0.5 truncate">{displayDesc}</p>}
        {item.dietary_tags?.length > 0 && (
          <div className="flex gap-1 mt-1 flex-wrap">
            {item.dietary_tags.map(tag => <span key={tag} className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{tagLabel(tag)}</span>)}
          </div>
        )}
      </div>
      {item.price != null && (
        <span className="font-medium text-foreground mx-4 shrink-0">&euro;{Number(item.price).toFixed(2)}</span>
      )}
      <div className="flex gap-1 shrink-0">
        <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`menu-item-edit-${item.item_id}`}><Edit className="h-3.5 w-3.5" /></Button>
        <Button variant="ghost" size="sm" onClick={onDelete} data-testid={`menu-item-delete-${item.item_id}`}><Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" /></Button>
      </div>
    </div>
  );
}

function CategoryForm({ onSuccess }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", name_el: "", description: "", description_el: "", sort_order: 0 });
  const [loading, setLoading] = useState(false);
  const handleSubmit = async e => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await axios.post(`${API}/owner/menu/categories`, form);
      toast.success(t("owner_menu:category_created", { defaultValue: "Category created" })); onSuccess(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || t("owner_menu:failed", { defaultValue: "Failed" })); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <BilingualInput
        label={t("owner_menu:field_name", { defaultValue: "Name" }) + " *"}
        value={form.name}
        valueEl={form.name_el}
        onChange={e => setForm({...form, name: e.target.value})}
        onChangeEl={e => setForm({...form, name_el: e.target.value})}
        required
        data-testid="menu-cat-name"
      />
      <BilingualTextarea
        label={t("owner_menu:field_description", { defaultValue: "Description" })}
        value={form.description}
        valueEl={form.description_el}
        onChange={e => setForm({...form, description: e.target.value})}
        onChangeEl={e => setForm({...form, description_el: e.target.value})}
        rows={2}
        data-testid="menu-cat-description"
      />
      <Button type="submit" disabled={loading} className="w-full" data-testid="menu-cat-submit">{loading ? t("owner_menu:creating", { defaultValue: "Creating..." }) : t("owner_menu:create_category", { defaultValue: "Create Category" })}</Button>
    </form>
  );
}

function ItemForm({ categories, dietaryTags = [], item, onSuccess }) {
  const isEdit = !!item;
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [form, setForm] = useState({
    category_id: item?.category_id || categories[0]?.category_id || "",
    name: item?.name || "", name_el: item?.name_el || "",
    description: item?.description || "", description_el: item?.description_el || "",
    price: item?.price || 0, dietary_tags: item?.dietary_tags || [],
    is_available: item?.is_available ?? true, sort_order: item?.sort_order || 0,
  });
  const [loading, setLoading] = useState(false);

  const toggleTag = slug => {
    setForm({ ...form, dietary_tags: form.dietary_tags.includes(slug) ? form.dietary_tags.filter(s => s !== slug) : [...form.dietary_tags, slug] });
  };

  const labelForTag = (tag) =>
    (lang === "el" && tag.name_el) || t(`dietary_tags.${tag.slug}`, { defaultValue: tag.name });

  const handleSubmit = async e => {
    e.preventDefault(); setLoading(true);
    try {
      const res = isEdit
        ? await axios.put(`${API}/owner/menu/items/${item.item_id}`, form)
        : await axios.post(`${API}/owner/menu/items`, form);
      toast.success(isEdit ? t("owner_menu:item_updated", { defaultValue: "Item updated" }) : t("owner_menu:item_created", { defaultValue: "Item created" })); onSuccess(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || t("owner_menu:failed", { defaultValue: "Failed" })); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>{t("owner_menu:field_category", { defaultValue: "Category" })}</Label>
        <Select value={form.category_id} onValueChange={v => setForm({...form, category_id: v})}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>{categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{getLocalized(c, "name", lang) || c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <BilingualInput
        label={t("owner_menu:field_name", { defaultValue: "Name" }) + " *"}
        value={form.name}
        valueEl={form.name_el}
        onChange={e => setForm({...form, name: e.target.value})}
        onChangeEl={e => setForm({...form, name_el: e.target.value})}
        required
        data-testid="menu-item-name"
      />
      <BilingualTextarea
        label={t("owner_menu:field_description", { defaultValue: "Description" })}
        value={form.description}
        valueEl={form.description_el}
        onChange={e => setForm({...form, description: e.target.value})}
        onChangeEl={e => setForm({...form, description_el: e.target.value})}
        rows={2}
        data-testid="menu-item-description"
      />
      <div className="grid grid-cols-2 gap-4">
        <div><Label>{t("owner_menu:field_price", { defaultValue: "Price (€)" })}</Label><Input type="number" step="0.01" value={form.price} onChange={e => setForm({...form, price: parseFloat(e.target.value) || 0})} className="mt-1" data-testid="menu-item-price-input" /></div>
        <div>
          <Label>{t("owner_menu:field_available", { defaultValue: "Available" })}</Label>
          <Select value={form.is_available ? "yes" : "no"} onValueChange={v => setForm({...form, is_available: v === "yes"})}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="yes">{t("owner_menu:available", { defaultValue: "Available" })}</SelectItem><SelectItem value="no">{t("owner_menu:unavailable", { defaultValue: "Unavailable" })}</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>{t("owner_menu:field_dietary_tags", { defaultValue: "Dietary Tags" })}</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {dietaryTags.map(tag => (
            <button key={tag.slug} type="button" onClick={() => toggleTag(tag.slug)}
              className={`px-2.5 py-1 rounded-full text-xs transition-colors ${form.dietary_tags.includes(tag.slug) ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
            >{labelForTag(tag)}</button>
          ))}
        </div>
      </div>
      <Button type="submit" disabled={loading} className="w-full" data-testid="menu-item-submit">
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} {isEdit ? t("owner_menu:update_item", { defaultValue: "Update Item" }) : t("owner_menu:add_item", { defaultValue: "Add Item" })}
      </Button>
    </form>
  );
}
